import React, { useState, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import NoDataFound from '$/components/global/noData';
import * as Bixby from '$/core';
import { DocumentsClientService, DocumentsAdminService } from '$/services';
import { toastError } from '$/store/toast/actions';
import { downloadFile } from '$/store/export/actions';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import { getIsCurrentUserDocHostingAdminState, getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import './index.scss';
import { Formik, Field, ErrorMessage, FormikProps } from 'formik';
import * as Yup from 'yup';
import InputBlock from '$/components/global/inputBlock';
import CompanyDocumentsCategoryEditor from '$/components/company-documents-category-editor';
import classnames from 'classnames';
import CompanyDocumentsCategoryPicker from '$/components/company-documents-category-picker';
import { GridApi } from 'ag-grid-enterprise';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import DocumentsGrid from '$/components/company-documents-grid/company-document-grid-template/grid';

interface ICompanyDocumentsGridTemplateProps {
  currentCompany?: Bixby.Models.AdminCompany;
  documents: Bixby.Models.CompanyDocument[];
  companyDocumentCategories: Bixby.Models.CompanyDocumentCategory[];
  isSuperAdmin: boolean;
  isDocHostingAdmin: boolean;
  isDeleteGrid?: boolean;
  isErrorGrid?: boolean;
  isPublishedGrid?: boolean;
  isPendingApprovalGrid?: boolean;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => Promise<void>;
  dispatchDownloadFailure: () => Promise<void>;
  getDocuments: () => Promise<void>;
}

interface EditDocumentValues {
  id: string;
  title: string;
  description: string;
  companyDocumentCompanyDocumentCategories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[];
}

interface EditDocumentTagValues {
  ids: string[];
  companyDocumentCompanyDocumentCategories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[];
}

const CompanyDocumentsGridTemplate: React.FC<ICompanyDocumentsGridTemplateProps> = props => {
  const {
    currentCompany,
    companyDocumentCategories,
    documents,
    dispatchDownloadDocument,
    dispatchDownloadFailure,
    isSuperAdmin,
    isDocHostingAdmin,
    isDeleteGrid,
    isErrorGrid,
    isPublishedGrid,
    isPendingApprovalGrid,
    getDocuments,
  } = props;
  const gridRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<Bixby.Models.CompanyDocument>();
  const [documentDeleting, setDocumentDeleting] = useState(false);
  const [documentDeleteSuccess, setDocumentDeleteSuccess] = useState(false);
  const [selectedModalData, setSelectedModalData] = useState<Bixby.Models.CompanyDocument[]>();
  const [documentsSelected, setDocumentsSelected] = useState(false);
  const [documentsSelectedSuccess, setDocumentsSelectedSuccess] = useState(false);
  const [documentsSelectedError, setDocumentsSelectedError] = useState(false);
  const [deletingAllType, setDeletingAllType] = useState<Bixby.Enums.CompanyDocumentState | undefined>(undefined);
  const [documentDeleteError, setDocumentDeleteError] = useState(undefined as string | undefined);
  const [editModalData, setEditModalData] = useState<EditDocumentValues>();
  const [addTagModalData, setAddTagModalData] = useState<EditDocumentTagValues>();
  const [documentSaving, setDocumentSaving] = useState(false);
  const [isCategoryFormShowing, setIsCategoryFormShowing] = useState(false);
  const [downloadedDocs, setDownloadedDocs] = useState<Set<string>>();
  const [gridApi, setGridApi] = useState(null as GridApi | null);
  const [notDownloadedModalData, setNotDownloadedModalData] = useState<Bixby.Models.CompanyDocument[]>();
  const [restoreModalData, setRestoreModalData] = useState<Bixby.Models.CompanyDocument>();
  const [documentRestoring, setDocumentRestoring] = useState(false);
  const [documentRestoreSuccess, setDocumentRestoreSuccess] = useState(false);
  const [documentRestoreError, setDocumentRestoreError] = useState(undefined as string | undefined);
  const [saveErrorMessage, setSaveErrorMessage] = useState(undefined as string | undefined);
  const [activeSelectedButton, setActiveSeletedButton] = useState(undefined as string | undefined);

  const handleDownload = async (documentId: string) => {
    try {
      const { blob, filename } = await DocumentsClientService.downloadDocument(documentId);
      // get rid of any escaped double quotes
      dispatchDownloadDocument(blob, (filename || '').replace(/["]/g, ''));

      setDownloadedDocs(prev => {
        const next = new Set(prev);
        next.add(documentId);
        return next;
      });
    } catch (err: any) {
      dispatchDownloadFailure();
    }
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);

    setTimeout(() => {
      setDeletingAllType(undefined);
      setDeleteModalData(undefined);
      setDocumentDeleting(false);
      setDocumentDeleteSuccess(false);
      setDocumentDeleteError(undefined);
    }, 1);
  };

  const handleDeleteDocument = async () => {
    if (!deleteModalData || !documents) return;

    try {
      setDocumentDeleting(true);
      if (deletingAllType === Bixby.Enums.CompanyDocumentState.PendingRequiresApproval && currentCompany?.id) {
        await DocumentsAdminService.deleteAllPendingDocuments(currentCompany?.id);
      } else if (deletingAllType === Bixby.Enums.CompanyDocumentState.Published && currentCompany?.id) {
        await DocumentsAdminService.deleteAllPublishedDocuments(currentCompany?.id);
      } else if (deletingAllType === Bixby.Enums.CompanyDocumentState.Deleted && currentCompany?.id) {
        await DocumentsAdminService.deleteAllDeletedDocumentsFromBlobStorage(currentCompany?.id);
      } else if (deletingAllType === Bixby.Enums.CompanyDocumentState.Error && currentCompany?.id) {
        await DocumentsAdminService.deleteAllErrorDocuments(currentCompany?.id);
      } else if (deleteModalData.state === Bixby.Enums.CompanyDocumentState.Deleted) {
        await DocumentsAdminService.deleteDocumentFromBlobStorage(deleteModalData.id);
      } else if (deleteModalData.state === Bixby.Enums.CompanyDocumentState.PendingRequiresApproval) {
        await DocumentsAdminService.deleteDocumentFromBlobStorage(deleteModalData.id);
      } else if (deleteModalData.state === Bixby.Enums.CompanyDocumentState.Error) {
        await DocumentsAdminService.deleteDocumentFromDatabase(deleteModalData.id);
      } else {
        await DocumentsAdminService.deleteDocument(deleteModalData.id);
      }
      getDocuments();
      handleDeleteModalClose();
    } catch (err: any) {
      setDocumentDeleting(false);
      setDocumentDeleteError('An error occurred');
    }
  };

  const handleDeleteAllModalOpen = (state: Bixby.Enums.CompanyDocumentState) => {
    if (!documents) return;
    setDeletingAllType(state);
    setDeleteModalData(documents[0]);
    setDocumentDeleting(false);
    setDocumentDeleteSuccess(false);
    setDocumentDeleteError(undefined);

    setTimeout(() => {
      setShowDeleteModal(true);
    }, 1);
  };

  const handleDeleteModalOpen = (model: Bixby.Models.CompanyDocument) => {
    setDeleteModalData(model);
    setDocumentDeleting(false);
    setDocumentDeleteSuccess(false);
    setDocumentDeleteError(undefined);

    setTimeout(() => {
      setShowDeleteModal(true);
    }, 1);
  };

  const handleEditModalOpen = (model: Bixby.Models.CompanyDocument) => {
    const { id, title, description, companyDocumentCompanyDocumentCategories } = model;
    setDocumentSaving(false);
    setSaveErrorMessage(undefined);
    setTimeout(() => {
      setEditModalData({ id, title, description, companyDocumentCompanyDocumentCategories });
    }, 1);
  };

  const handleAddTagModalOpen = (documents: Bixby.Models.CompanyDocument[]) => {
    const ids = documents.map(_ => _.id);
    const companyDocumentCompanyDocumentCategories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[] = [];
    //sets model with selected ids
    setDocumentSaving(false);
    setTimeout(() => {
      setAddTagModalData({ ids, companyDocumentCompanyDocumentCategories });
    }, 1);
  };

  const handleRemoveTags = async () => {
    if (!selectedModalData) return;

    try {
      setDocumentsSelected(true);
      const ids = selectedModalData.map(_ => _.id);
      const companyDocumentCompanyDocumentCategories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[] = [];
      await DocumentsAdminService.BulkUpdateDocumentTag({ ids, companyDocumentCompanyDocumentCategories });
      setDocumentsSelectedSuccess(true);
      getDocuments();
    } catch (err: any) {
      setDocumentsSelected(false);
      setDocumentsSelectedError(true);
    }
  };

  const handleApproveDocuments = async () => {
    if (!selectedModalData) return;

    try {
      setDocumentsSelected(true);
      const ids = selectedModalData.map(_ => _.id);
      await DocumentsAdminService.approveDocuments(ids);
      setDocumentsSelectedSuccess(true);
      getDocuments();
    } catch (err: any) {
      setDocumentsSelected(false);
      setDocumentsSelectedError(true);
    }
  };

  const handleSelectedDeletedDocuments = async () => {
    if (!selectedModalData) return;

    try {
      setDocumentsSelected(true);
      const ids = selectedModalData.map(_ => _.id);
      await DocumentsAdminService.deleteSelectedDocuments(ids);
      setDocumentsSelectedSuccess(true);
      getDocuments();
    } catch (err: any) {
      setDocumentsSelected(false);
      setDocumentsSelectedError(true);
    }
  };

  const handleSelectedModalClose = () => {
    if (downloadedDocs) setDocumentsSelected(false);
    setDocumentsSelectedSuccess(false);
    setDocumentsSelectedError(false);
    setSelectedModalData(undefined);
    setNotDownloadedModalData(undefined);
    setActiveSeletedButton(undefined);
  };

  const handleSelectedModalOpen = (ids: Bixby.Models.CompanyDocument[]) => {
    const notDownloaded: Bixby.Models.CompanyDocument[] = [];

    //if any select not in downloadedDocs show model of not approved
    ids?.forEach(document => {
      if (!downloadedDocs?.has(document.id)) {
        notDownloaded.push(document);
      }
    });
    if (activeSelectedButton == 'approve' && notDownloaded && notDownloaded.length > 0) {
      setNotDownloadedModalData(notDownloaded);
      return;
    } else {
      setDocumentsSelected(false);
      setDocumentsSelectedSuccess(false);
      setDocumentsSelectedError(false);

      setTimeout(() => {
        setSelectedModalData(ids);
      }, 1);
    }
  };

  //restore
  const handleRestoreModalOpen = (model: Bixby.Models.CompanyDocument) => {
    setRestoreModalData(model);
    setDocumentRestoring(false);
    setDocumentRestoreSuccess(false);
    setDocumentRestoreError(undefined);

    setTimeout(() => {
      setRestoreModalData(model);
    }, 1);
  };

  const handleRestoreModalClose = () => {
    setTimeout(() => {
      setRestoreModalData(undefined);
      setDocumentRestoring(false);
      setDocumentRestoreSuccess(false);
      setDocumentRestoreError(undefined);
    }, 1);
  };

  const handleRestoreDocument = async () => {
    if (!restoreModalData) return;

    try {
      setDocumentRestoring(true);
      await DocumentsAdminService.restoreDocument(restoreModalData.id);
      setDocumentRestoreSuccess(true);
      getDocuments();
    } catch (err: any) {
      setDocumentRestoring(false);
      setDocumentRestoreError('An error occurred');
    }
  };

  const updateInlineData = (editModel: EditDocumentValues) => {
    const editedRow = gridApi?.getRowNode(editModel.id);

    editedRow?.setDataValue('description', editModel?.description);
    editedRow?.setDataValue(
      'companyDocumentCompanyDocumentCategories',
      editModel?.companyDocumentCompanyDocumentCategories
    );
  };

  const formatCompanyDocumentCompanyDocumentCategory = (
    categoryName: string,
    financialDocumentType?: string,
    financialDocumentDate?: Date,
    orgChartPageNumber?: number
  ) => {
    if (financialDocumentType === Bixby.Enums.FinancialDocumentType.Q) {
      let month = dayjs(financialDocumentDate).month();
      month += 1;
      let year = dayjs(financialDocumentDate).year();
      if (currentCompany?.fiscalYearEnd && currentCompany?.fiscalYearEnd !== null) {
        const fyeMonth = dayjs(currentCompany?.fiscalYearEnd).month() + 1;
        const fyeAdjustment = 12 - fyeMonth;
        if (month + fyeAdjustment > 12) {
          month = month + fyeAdjustment - 12;
          year = year + 1;
        } else {
          month = month + fyeAdjustment;
        }
      }
      const quarter = month < 4 ? '1' : month >= 4 && month < 7 ? '2' : month >= 7 && month < 10 ? '3' : '4';
      return ` ${categoryName} (${financialDocumentType}${quarter}-${year.toString().substring(2, 4)} - ${dayjs(
        financialDocumentDate
      ).format('MMM-YY')})`;
    } else if (financialDocumentType === Bixby.Enums.FinancialDocumentType.FYE) {
      return ` ${categoryName} (${financialDocumentType} - ${dayjs(financialDocumentDate).format('MMM-YY')})`;
    } else if (orgChartPageNumber) {
      return ` ${categoryName} (Page ${orgChartPageNumber})`;
    } else {
      return categoryName;
    }
  };

  const clientsWithPendingDocs = useMemo(() => {
    // implement
    if (!isPendingApprovalGrid) return;
    return [...new Set(documents.map(_ => _.client_Name))];
  }, [isPendingApprovalGrid, documents]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const formikRef = useRef<FormikProps<EditDocumentValues>>(null);

  const editDocumentValidationSchema = Yup.object({
    title: Yup.string().required('Title is required.'),
    description: Yup.string().notRequired(),
  });

  return (
    <>
      <div className="grid-title-contain">
        {isPendingApprovalGrid && (
          <>
            <h3>Requires Approval</h3>
            <div className="grid-action">
              <BixButton
                light
                type="button"
                disabled={!documents || documents.length === 0}
                onClick={() => {
                  if (!gridRef?.current) return;
                  setActiveSeletedButton('remove');
                  handleSelectedModalOpen((gridRef.current as any).api.getSelectedRows());
                }}>
                Remove Selected Tags
              </BixButton>
              <BixButton
                light
                type="button"
                disabled={!documents || documents.length === 0}
                onClick={() => {
                  if (!gridRef?.current) return;
                  handleAddTagModalOpen((gridRef.current as any).api.getSelectedRows());
                }}>
                Tag Selected
              </BixButton>
              {isDocHostingAdmin && (
                <div className="grid-action">
                  <BixButton
                    type="button"
                    disabled={!documents || documents.length === 0}
                    onClick={() => {
                      if (!gridRef?.current) return;
                      setActiveSeletedButton('approve');
                      handleSelectedModalOpen((gridRef.current as any).api.getSelectedRows());
                    }}>
                    Approve Selected
                  </BixButton>
                  <div className="grid-action">
                    <BixButton
                      type="button"
                      disabled={!documents || documents.length === 0}
                      onClick={() =>
                        handleDeleteAllModalOpen(Bixby.Enums.CompanyDocumentState.PendingRequiresApproval)
                      }>
                      Delete All Pending
                    </BixButton>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {isPublishedGrid && (
          <>
            <h3>Published</h3>
            <div className="grid-action">
              <BixButton
                light
                type="button"
                disabled={!documents || documents.length === 0}
                onClick={() => {
                  if (!gridRef?.current) return;
                  setActiveSeletedButton('remove');
                  handleSelectedModalOpen((gridRef.current as any).api.getSelectedRows());
                }}>
                Remove Selected Tags
              </BixButton>
              <BixButton
                light
                type="button"
                disabled={!documents || documents.length === 0}
                onClick={() => {
                  if (!gridRef?.current) return;
                  handleAddTagModalOpen((gridRef.current as any).api.getSelectedRows());
                }}>
                Tag Selected
              </BixButton>
              {(isSuperAdmin || isDocHostingAdmin) && (
                <BixButton
                  type="button"
                  onClick={() => handleDeleteAllModalOpen(Bixby.Enums.CompanyDocumentState.Published)}
                  disabled={!documents || documents.length === 0}>
                  Delete All Published
                </BixButton>
              )}
            </div>
          </>
        )}
        {isErrorGrid && (
          <>
            <h3>Error</h3>
            <div className="grid-action">
              <BixButton
                type="button"
                onClick={() => handleDeleteAllModalOpen(Bixby.Enums.CompanyDocumentState.Error)}
                disabled={!documents || documents.length === 0}>
                Delete All Error
              </BixButton>
            </div>
          </>
        )}
        {isDeleteGrid && (
          <>
            <h3>Recycle Bin</h3>
            {isDocHostingAdmin && (
              <div className="grid-action">
                <BixButton
                  type="button"
                  disabled={!documents || documents.length === 0}
                  onClick={() => {
                    if (!gridRef?.current) return;
                    setActiveSeletedButton('clear');
                    handleSelectedModalOpen((gridRef.current as any).api.getSelectedRows());
                  }}>
                  Clear Selected
                </BixButton>
                <BixButton
                  type="button"
                  onClick={() => handleDeleteAllModalOpen(Bixby.Enums.CompanyDocumentState.Deleted)}
                  disabled={!documents || documents.length === 0}>
                  Clear Bin
                </BixButton>
              </div>
            )}
          </>
        )}
      </div>
      {currentCompany && documents && !Bixby.Utils.isNullOrEmpty(documents) ? (
        <>
          {isPendingApprovalGrid && (
            <Tabs>
              <TabList>
                {clientsWithPendingDocs?.map(clientName => (
                  <Tab>{clientName}</Tab>
                ))}
              </TabList>
              {clientsWithPendingDocs?.map(clientName => (
                <TabPanel>
                  <DocumentsGrid
                    currentCompany={currentCompany}
                    documents={documents.filter(_ => _.client_Name == clientName)}
                    isDeleteGrid={isDeleteGrid}
                    isErrorGrid={isErrorGrid}
                    isPendingApprovalGrid={isPendingApprovalGrid}
                    isDocHostingAdmin={isDocHostingAdmin}
                    downloadedDocs={downloadedDocs}
                    formatCompanyDocumentCompanyDocumentCategory={formatCompanyDocumentCompanyDocumentCategory}
                    handleDownload={handleDownload}
                    handleEditModalOpen={handleEditModalOpen}
                    handleRestoreModalOpen={handleRestoreModalOpen}
                    handleDeleteModalOpen={handleDeleteModalOpen}
                    handleSelectedModalOpen={handleSelectedModalOpen}
                    onGridReady={onGridReady}
                    gridRef={gridRef}
                  />
                </TabPanel>
              ))}
            </Tabs>
          )}
          {!isPendingApprovalGrid && (
            <DocumentsGrid
              currentCompany={currentCompany}
              documents={documents}
              isDeleteGrid={isDeleteGrid}
              isErrorGrid={isErrorGrid}
              isPendingApprovalGrid={isPendingApprovalGrid}
              isDocHostingAdmin={isDocHostingAdmin}
              downloadedDocs={downloadedDocs}
              formatCompanyDocumentCompanyDocumentCategory={formatCompanyDocumentCompanyDocumentCategory}
              handleDownload={handleDownload}
              handleEditModalOpen={handleEditModalOpen}
              handleRestoreModalOpen={handleRestoreModalOpen}
              handleDeleteModalOpen={handleDeleteModalOpen}
              handleSelectedModalOpen={handleSelectedModalOpen}
              onGridReady={onGridReady}
              gridRef={gridRef}
            />
          )}
        </>
      ) : (
        <NoDataFound
          dataType={isDeleteGrid ? 'deleted' : isErrorGrid ? 'error' : isPendingApprovalGrid ? 'approval' : 'published'}
        />
      )}
      {editModalData && (
        <Modal active={true} title="Edit Document" hideClose>
          <div className="edit-document-modal">
            <div>
              <Formik
                innerRef={formikRef}
                initialValues={editModalData}
                validationSchema={editDocumentValidationSchema}
                onSubmit={async (values: EditDocumentValues) => {
                  try {
                    setSaveErrorMessage(undefined);
                    setDocumentSaving(true);
                    await DocumentsAdminService.updateDocument(editModalData.id, values);
                    updateInlineData(values);
                    setEditModalData(undefined);
                  } catch (err: any) {
                    const message =
                      err && err.response && err.response.data && err.response.data.error
                        ? err.response.data.error
                        : 'An unknown error occurred.';
                    setSaveErrorMessage(message);
                  } finally {
                    setDocumentSaving(false);
                  }
                }}
                render={({ handleSubmit, values, setFieldValue, isValid, dirty }) => (
                  <form className="create-company-form" onSubmit={handleSubmit}>
                    <div className={classnames({ hidden: isCategoryFormShowing })}>
                      <div>
                        <InputBlock title="Title">
                          <Field type="text" name="title" />
                        </InputBlock>
                        <ErrorMessage name="bixbyId">
                          {msg => <div className="error error-message">{msg}</div>}
                        </ErrorMessage>
                        <InputBlock title="Description">
                          <Field type="text" name="description" />
                        </InputBlock>
                        <p className="category-label">Categories</p>
                        <CompanyDocumentsCategoryEditor
                          documentId={editModalData.id}
                          companyDocumentCompanyDocumentCategories={values.companyDocumentCompanyDocumentCategories}
                          isEditable={true}
                          onEditCategory={() => {
                            setIsCategoryFormShowing(true);
                          }}
                          onDeleteCategory={(
                            categories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[] | undefined
                          ) => setFieldValue('companyDocumentCompanyDocumentCategories', categories)}
                        />
                      </div>
                      {saveErrorMessage && (
                        <div className="server-error error error-message">{`An error occurred trying to save the document: ${saveErrorMessage}`}</div>
                      )}
                      <div className="button_container">
                        <BixButton
                          type="submit"
                          useLoading
                          isLoading={documentSaving}
                          disabled={documentSaving || (!isValid && dirty)}>
                          Save
                        </BixButton>
                        <BixButton
                          light
                          type="button"
                          disabled={documentSaving}
                          onClick={() => setEditModalData(undefined)}>
                          Cancel
                        </BixButton>
                      </div>
                    </div>
                    {isCategoryFormShowing && (
                      <CompanyDocumentsCategoryPicker
                        id={editModalData.id}
                        allCategories={companyDocumentCategories}
                        currentCompanyDocumentCategories={values.companyDocumentCompanyDocumentCategories}
                        onSave={(_id: string, categories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[]) => {
                          setFieldValue('companyDocumentCompanyDocumentCategories', categories);
                          setIsCategoryFormShowing(false);
                        }}
                        onCancel={() => setIsCategoryFormShowing(false)}
                        fiscalYearEnd={currentCompany?.fiscalYearEnd}
                      />
                    )}
                  </form>
                )}></Formik>
            </div>
          </div>
        </Modal>
      )}
      {addTagModalData && (
        <Modal active={true} title="Edit Tag" hideClose>
          <div className="edit-document-modal">
            <div>
              <Formik
                initialValues={addTagModalData}
                onSubmit={async (values: EditDocumentTagValues) => {
                  try {
                    setSaveErrorMessage(undefined);
                    setDocumentSaving(true);
                    await DocumentsAdminService.BulkUpdateDocumentTag(values);
                    setAddTagModalData(undefined);
                    getDocuments();
                  } catch (err: any) {
                    const message =
                      err && err.response && err.response.data && err.response.data.error
                        ? err.response.data.error
                        : 'An unknown error occurred.';
                    setSaveErrorMessage(message);
                  } finally {
                    setDocumentSaving(false);
                  }
                }}
                render={({ handleSubmit, values, setFieldValue, isValid, dirty }) => (
                  <form className="create-company-form" onSubmit={handleSubmit}>
                    <div className={classnames({ hidden: isCategoryFormShowing })}>
                      <div>
                        <p>
                          Add tag to the {values.ids.length} selected{' '}
                          {Bixby.Utils.pluralizeStr(values.ids.length, 'document')}.
                        </p>
                        <p className="category-label">Categories</p>
                        <CompanyDocumentsCategoryEditor
                          documentId={''}
                          companyDocumentCompanyDocumentCategories={values.companyDocumentCompanyDocumentCategories}
                          isEditable={true}
                          onEditCategory={() => {
                            setIsCategoryFormShowing(true);
                          }}
                          onDeleteCategory={(
                            categories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[] | undefined
                          ) => setFieldValue('companyDocumentCompanyDocumentCategories', categories)}
                        />
                      </div>
                      {saveErrorMessage && (
                        <div className="server-error error error-message">{`An error occurred trying to save the document: ${saveErrorMessage}`}</div>
                      )}
                      <div className="button_container">
                        <BixButton
                          type="submit"
                          useLoading
                          isLoading={documentSaving}
                          disabled={documentSaving || (!isValid && dirty)}>
                          Save
                        </BixButton>
                        <BixButton
                          light
                          type="button"
                          disabled={documentSaving}
                          onClick={() => setAddTagModalData(undefined)}>
                          Cancel
                        </BixButton>
                      </div>
                    </div>
                    {isCategoryFormShowing && (
                      <CompanyDocumentsCategoryPicker
                        id={''}
                        allCategories={companyDocumentCategories}
                        currentCompanyDocumentCategories={values.companyDocumentCompanyDocumentCategories}
                        onSave={(_id: string, categories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[]) => {
                          setFieldValue('companyDocumentCompanyDocumentCategories', categories);
                          setIsCategoryFormShowing(false);
                        }}
                        onCancel={() => setIsCategoryFormShowing(false)}
                        fiscalYearEnd={currentCompany?.fiscalYearEnd}
                      />
                    )}
                  </form>
                )}></Formik>
            </div>
          </div>
        </Modal>
      )}
      {deleteModalData && (
        <Modal active={showDeleteModal} hideClose>
          <div className="reupload_modal_content">
            {!documentDeleteSuccess && (
              <>
                {deleteModalData.client_Name ? (
                  <p>
                    Are you sure you want to delete {!deletingAllType ? deleteModalData.title : 'all files'} for client{' '}
                    {deleteModalData.client_Name}?
                  </p>
                ) : (
                  <p>Are you sure you want to delete {!deletingAllType ? deleteModalData.title : 'all files'}?</p>
                )}
                {documentDeleteError && <p className="error_msg">{documentDeleteError}</p>}
              </>
            )}
            {documentDeleteSuccess && (
              <>
                <p>Deleted the document {deleteModalData.title}</p>
                <BixButton onClick={handleDeleteModalClose}>Close</BixButton>
              </>
            )}
            <div className="modal_button_container">
              <BixButton
                type="button"
                onClick={() => handleDeleteDocument()}
                useLoading
                isLoading={documentDeleting}
                disabled={documentDeleting}>
                Confirm
              </BixButton>
              <BixButton light type="button" onClick={() => handleDeleteModalClose()} disabled={documentDeleting}>
                Cancel
              </BixButton>
            </div>
          </div>
        </Modal>
      )}
      {selectedModalData && (
        <Modal active={true} hideClose>
          <div className="reupload_modal_content">
            {!documentsSelectedSuccess && (
              <>
                <p>
                  Please confirm that you want to{' '}
                  {activeSelectedButton == 'clear'
                    ? ' clear'
                    : activeSelectedButton == 'remove'
                    ? 'remove tags for'
                    : ' approve'}{' '}
                  {selectedModalData.length} {Bixby.Utils.pluralizeStr(selectedModalData.length, 'document')}.
                </p>
                {documentsSelectedError && <p className="error_msg">{documentsSelectedError}</p>}
                <div className="modal_button_container">
                  <BixButton
                    type="button"
                    onClick={
                      activeSelectedButton == 'clear'
                        ? handleSelectedDeletedDocuments
                        : activeSelectedButton == 'remove'
                        ? handleRemoveTags
                        : handleApproveDocuments
                    }
                    useLoading
                    isLoading={documentsSelected}
                    disabled={documentsSelected}>
                    Confirm
                  </BixButton>
                  <BixButton
                    light
                    type="button"
                    onClick={() => handleSelectedModalClose()}
                    disabled={documentsSelected}>
                    Cancel
                  </BixButton>
                </div>
              </>
            )}
            {documentsSelectedSuccess && (
              <>
                <p>
                  Successfully{' '}
                  {activeSelectedButton == 'clear'
                    ? ' cleared'
                    : activeSelectedButton == 'remove'
                    ? 'remvoed tags for'
                    : ' approved'}{' '}
                  {selectedModalData.length} {Bixby.Utils.pluralizeStr(selectedModalData.length, 'document')}
                </p>
                <BixButton onClick={handleSelectedModalClose}>Close</BixButton>
              </>
            )}
          </div>
        </Modal>
      )}
      {notDownloadedModalData && (
        <Modal active={true} hideClose>
          <div className="reupload_modal_content">
            <>
              <p>
                The following documents have not been downloaded. Please download then retry approval.
                {notDownloadedModalData.map((doc, index) => (
                  <li key={index} value={doc.id}>
                    {' '}
                    {doc.fileName}{' '}
                  </li>
                ))}
              </p>
              <div className="modal_button_container">
                <BixButton light type="button" onClick={() => handleSelectedModalClose()} disabled={documentsSelected}>
                  Cancel
                </BixButton>
              </div>
            </>
          </div>
        </Modal>
      )}
      {restoreModalData && (
        <Modal active={true} hideClose>
          <div className="reupload_modal_content">
            {!documentRestoreSuccess && (
              <>
                <p>Please confirm that you want to restore {restoreModalData.fileName}.</p>
                {documentRestoreError && <p className="error_msg">{documentRestoreError}</p>}
                <div className="modal_button_container">
                  <BixButton
                    type="button"
                    onClick={handleRestoreDocument}
                    useLoading
                    isLoading={documentRestoring}
                    disabled={documentRestoring}>
                    Confirm
                  </BixButton>
                  <BixButton light type="button" onClick={() => handleRestoreModalClose()} disabled={documentRestoring}>
                    Cancel
                  </BixButton>
                </div>
              </>
            )}
            {documentRestoreSuccess && (
              <>
                <p>Successfully restored {restoreModalData.fileName}</p>
                <BixButton onClick={handleRestoreModalClose}>Close</BixButton>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isDocHostingAdmin } = getIsCurrentUserDocHostingAdminState(state);
  return {
    isSuperAdmin,
    isDocHostingAdmin,
    currentCompany: state.companyState.currentCompany,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadDocument: (blob: Blob, fileName: string) => dispatch(downloadFile(blob, fileName)),
  dispatchDownloadFailure: () => dispatch(toastError('Failed to download document')),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDocumentsGridTemplate);
